import React, { useState } from 'react';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  actions?: (item: T) => React.ReactNode;
}

export default function DataTable<T extends { id: string }>({
  data,
  columns,
  onRowClick,
  selectable,
  onSelectionChange,
  actions,
}: DataTableProps<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = new Set(data.map(item => item.id));
      setSelectedIds(newSelected);
      onSelectionChange?.(data);
    } else {
      setSelectedIds(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (item: T, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(item.id);
    } else {
      newSelected.delete(item.id);
    }
    setSelectedIds(newSelected);
    const selectedItems = data.filter(d => newSelected.has(d.id));
    onSelectionChange?.(selectedItems);
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;
    
    return [...data].sort((a, b) => {
      const aValue = (a as any)[sortConfig.key];
      const bValue = (b as any)[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-calm-border">
        <thead className="bg-slate-50/50">
          <tr>
            {selectable && (
              <th className="px-2 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.size === data.length && data.length > 0}
                  onChange={handleSelectAll}
                    className="rounded-sm border-calm-border text-calm-text focus:ring-calm-text/30"
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-2 py-3 text-left text-xs font-light text-calm-muted uppercase tracking-wider cursor-pointer hover:bg-slate-50"
                onClick={() => column.sortable && handleSort(column.key)}
              >
                <div className="flex items-center space-x-1">
                  <span>{column.header}</span>
                  {column.sortable && sortConfig?.key === column.key && (
                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            ))}
            {actions && <th className="px-2 py-3 text-xs font-light text-calm-muted uppercase tracking-wider">Acciones</th>}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-calm-border">
          {sortedData.map((item) => (
            <tr
              key={item.id}
              className={`hover:bg-slate-50/50 ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={() => onRowClick?.(item)}
            >
              {selectable && (
                <td className="px-2 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={(e) => handleSelectRow(item, e.target.checked)}
                    className="rounded-sm border-calm-border text-calm-text focus:ring-calm-text/30"
                  />
                </td>
              )}
              {columns.map((column) => (
                <td key={column.key} className="px-2 py-4 whitespace-nowrap text-sm text-calm-text">
                  {column.render ? column.render(item) : (item as any)[column.key]}
                </td>
              ))}
              {actions && (
                <td className="px-2 py-4 whitespace-nowrap text-center text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                  {actions(item)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-12 text-calm-muted">
          No se encontraron resultados
        </div>
      )}
    </div>
  );
}
